import { firstValueFrom } from 'rxjs'
import connectRefinementList, {
    RefinementListConnectorParams as CParams,
    RefinementListWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { Renderer } from 'instantsearch.js/es/types'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'

export interface FilterItem {
    value: string
    label: string
}

@Component({
    selector: 'app-refinement-list-base',
    templateUrl: './refinement-list-base.component.html',
})
export class RefinementListBaseComponent extends AbstractInstantsearchWidgetComponent<
WDescription,
CParams
> implements OnInit, OnDestroy {
    public override readonly widgetName = 'RefinementList'

    @Input()
    public attribute: string

    @Input()
    public localisedAttribute: string

    @Input()
    public operator: 'and' | 'or'

    @Input()
    public instantSearchInstance: AlgoliaInstantsearchComponent

    @Input()
    public initialiseFromUrl: boolean = true

    @Input()
    public customLabels?: FilterItem[]

    constructor(
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super()
    }

    public override ngOnInit(): void {
        this.createWidget(connectRefinementList, {
            attribute: this.attribute,
            operator: this.operator,
            limit: 3,
            showMore: true,
            showMoreLimit: 20,
        })

        super.ngOnInit()
    }

    private async initialiseRefinementFromUrl(): Promise<void> {
        const queryParams = await firstValueFrom(this.route.queryParamMap)
        const refinementValues = queryParams.get(
            this.localisedAttribute?.toLowerCase() ?? this.attribute,
        )?.split(',') ?? []

        refinementValues.forEach((v) => this.state!.refine(v))
    }

    public toggleItem(item: string): void {
        this.state?.refine(item)
        this.setUrlPart(item)
    }

    private setUrlPart(item: string): void {
        void this.router.navigate([],
            {
                relativeTo: this.route,
                queryParams: {
                    [this.localisedAttribute?.toLowerCase() ?? this.attribute]: this.state?.items
                        .filter((i) => i.value === item ? ! i.isRefined : i.isRefined)
                        .map((i) => i.value).join(',') || undefined,
                },
                queryParamsHandling: 'merge', // remove to replace all query params by provided
            },
        )
    }

    public override updateState: Renderer<WDescription['renderState'], CParams> = (state, isFirstRendering) => {
        if (isFirstRendering) {
            Promise.resolve().then(() => {
                this.state = state
                if (this.initialiseFromUrl) {
                    void this.initialiseRefinementFromUrl()
                }
            })
        } else {
            this.state = state
        }

        if (! this.customLabels) {
            return
        }

        this.state?.items.forEach((item) => {
            const customLabel = this.customLabels!
                .find((label) => label.value === item.value)

            if (customLabel) {
                item.label = customLabel.label
            }
        })
    }

    public override ngOnDestroy() {
        return
    }
}
